import React, { useCallback, useMemo } from 'react';
import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import ListSubheader from '@mui/material/ListSubheader';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import FormatTextdirectionLToRIcon from '@mui/icons-material/FormatTextdirectionLToR';
import FormatTextdirectionRToLIcon from '@mui/icons-material/FormatTextdirectionRToL';
import Color from '@tiptap/extension-color';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import TextStyle from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import StarterKit from '@tiptap/starter-kit';
import {
    MenuButton,
    LinkBubbleMenu,
    LinkBubbleMenuHandler,
    MenuButtonBold,
    MenuButtonBulletedList,
    MenuButtonEditLink,
    MenuButtonItalic,
    MenuButtonOrderedList,
    MenuButtonRedo,
    MenuButtonTextColor,
    MenuButtonUnderline,
    MenuButtonUndo,
    MenuControlsContainer,
    MenuDivider,
    MenuSelectTextAlign,
    RichTextEditor,
    type RichTextEditorRef
} from 'mui-tiptap';

import { MERGE_FIELDS, type MergeFieldDefinition } from '../../utils/mergeFields';

interface Props {
    value: string;
    onChange: (html: string) => void;
    dir: 'rtl' | 'ltr';
    /** Direction lives in the toolbar, beside alignment, as it does in Google Docs. */
    onDirChange: (dir: 'rtl' | 'ltr') => void;
    /**
     * Changes when `value` is replaced from outside — a different template, or a
     * different language of the same one.
     *
     * `RichTextEditor` treats `content` as *initial* content and documents that later
     * changes do not re-render it, so without this a selected template would fill the
     * subject and leave the previous body on screen. Passing it through
     * `editorDependencies` rebuilds the editor with the new content. It must **not**
     * change while the user types, or every keystroke would reset the editor.
     */
    contentKey?: string;
}

/**
 * The message editor.
 *
 * The toolbar is deliberately **narrow**: bold, italic, underline, colour, lists,
 * alignment and links. Everything an email client renders reliably, and nothing it
 * does not — no tables, no floated images, no custom block layout. Outlook supports
 * neither flexbox nor grid, and the branded wrapper already owns the page layout, so
 * offering those controls would only let an author build something that looks right
 * here and breaks in a recipient's inbox.
 *
 * Merge fields are inserted from a single menu rather than a wall of chips: there are
 * thirteen of them, most only relevant in one context, and a chip row that long is
 * noise on every template regardless of whether fields are being used.
 *
 * Shared by the Email Templates page (authoring) and the compose dialog (sending). A
 * template body is HTML, so the send path must edit it with the same editor that wrote
 * it — a plain textarea there would show the sender raw markup and let them corrupt it.
 */
const MessageEditor: React.FC<Props> = ({ value, onChange, dir, onDirChange, contentKey }) => {
    const editorRef = React.useRef<RichTextEditorRef>(null);
    const [fieldMenuAnchor, setFieldMenuAnchor] = React.useState<null | HTMLElement>(null);

    const extensions = useMemo(
        () => [
            StarterKit.configure({
                // Headings would fight the wrapper's typography; an email body is prose.
                heading: false,
                codeBlock: false,
                blockquote: false,
                horizontalRule: false
            }),
            Underline,
            TextStyle,
            Color,
            TextAlign.configure({ types: ['paragraph'] }),
            Link.configure({ openOnClick: false, autolink: true }),
            // Supplies the popover that MenuButtonEditLink opens for entering a URL.
            LinkBubbleMenuHandler
        ],
        []
    );

    const insertField = useCallback((field: MergeFieldDefinition) => {
        // Inserted as plain text: the placeholder is resolved before the body is
        // turned into HTML, so it must survive as literal characters.
        editorRef.current?.editor?.chain().focus().insertContent(`<${field.name}>`).run();
        setFieldMenuAnchor(null);
    }, []);

    const grouped = useMemo(() => {
        const groups: Record<string, MergeFieldDefinition[]> = { Always: [], 'When sending a document': [], 'Automated engagements': [] };
        MERGE_FIELDS.forEach((f) => {
            if (f.scope === 'both') groups.Always.push(f);
            else if (f.scope === 'document') groups['When sending a document'].push(f);
            else groups['Automated engagements'].push(f);
        });
        return groups;
    }, []);

    // Direction is applied to the editable area only, never to the wrapper: putting
    // `dir` on the container mirrors the toolbar too, so every button jumps to the
    // other side the moment the author switches language. Google Docs keeps its
    // toolbar fixed for the same reason.
    return (
        <Box
            sx={{
                '& .ProseMirror': {
                    minHeight: 260,
                    direction: dir,
                    textAlign: dir === 'rtl' ? 'right' : 'left'
                },
                '& .MuiTiptap-FieldContainer-root': { borderRadius: 2 }
            }}
        >
            <RichTextEditor
                ref={editorRef}
                extensions={extensions}
                content={value}
                editorDependencies={[contentKey]}
                editable
                onUpdate={({ editor }) => onChange(editor.getHTML())}
                renderControls={() => (
                    <MenuControlsContainer>
                        <MenuButtonBold />
                        <MenuButtonItalic />
                        <MenuButtonUnderline />
                        <MenuButtonTextColor defaultTextColor="#1a2330" />
                        <MenuDivider />
                        <MenuSelectTextAlign />
                        {/* Direction sits next to alignment, matching Google Docs — an
                            author writing Hebrew reaches for it in the same place. */}
                        <MenuButton
                            tooltipLabel="Left to right"
                            IconComponent={FormatTextdirectionLToRIcon}
                            selected={dir === 'ltr'}
                            onClick={() => onDirChange('ltr')}
                        />
                        <MenuButton
                            tooltipLabel="Right to left"
                            IconComponent={FormatTextdirectionRToLIcon}
                            selected={dir === 'rtl'}
                            onClick={() => onDirChange('rtl')}
                        />
                        <MenuButtonBulletedList />
                        <MenuButtonOrderedList />
                        <MenuDivider />
                        <MenuButtonEditLink />
                        <MenuDivider />
                        <Button
                            size="small"
                            variant="text"
                            endIcon={<KeyboardArrowDownIcon />}
                            onClick={(e) => setFieldMenuAnchor(e.currentTarget)}
                        >
                            Insert field
                        </Button>
                        <MenuDivider />
                        <MenuButtonUndo />
                        <MenuButtonRedo />
                    </MenuControlsContainer>
                )}
                RichTextFieldProps={{ variant: 'outlined' }}
            >
                {() => <LinkBubbleMenu />}
            </RichTextEditor>

            <Menu
                anchorEl={fieldMenuAnchor}
                open={Boolean(fieldMenuAnchor)}
                onClose={() => setFieldMenuAnchor(null)}
                slotProps={{ paper: { sx: { maxHeight: 420 } } }}
            >
                {Object.entries(grouped).map(([label, fields], index) =>
                    fields.length
                        ? [
                              index > 0 ? <Divider key={`${label}-divider`} /> : null,
                              <ListSubheader key={label} sx={{ lineHeight: '32px' }}>
                                  {label}
                              </ListSubheader>,
                              ...fields.map((f) => (
                                  <MenuItem key={f.name} onClick={() => insertField(f)} dense>
                                      {f.name}
                                  </MenuItem>
                              ))
                          ]
                        : null
                )}
            </Menu>
        </Box>
    );
};

export default MessageEditor;
